import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import Badge from "antd/es/badge";
import Tag from "antd/es/tag";
import Tooltip from "antd/es/tooltip";
import { defineMessages, useIntl } from "react-intl";

import { EntityType, EntityTypesSupportingChildLayouts } from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import FormLayout, { ChildLayout } from "@mapmycustomers/shared/types/layout/FormLayout";
import { Button } from "@mapmycustomers/ui";

import ActivityTypeIcon from "@app/component/activity/ActivityTypeIcon";
import ActivityRequiredRelationshipAlert from "@app/scene/settings/component/FormLayouts/component/ActivityRequiredRelationshipAlert";
import {
  getActiveEntityType,
  getEditedChildLayoutId,
  getEditedLayout,
} from "@app/scene/settings/component/FormLayouts/store";
import { selectChildLayout } from "@app/scene/settings/component/FormLayouts/store/actions";
import { getActivityTypes } from "@app/store/activity";
import { getFunnelStages } from "@app/store/deal";
import { RootState } from "@app/store/rootReducer";
import colors from "@app/styles/_colors.module.scss";
import anyColorToHex from "@app/util/colors/anyColorToHex";

import styles from "./VariantBanner.module.scss";

interface Props {
  activeChildLayout?: ChildLayout["id"];
  activityTypes: ActivityType[];
  entityType: EntityTypesSupportingChildLayouts;
  funnelStages: Record<Funnel["id"], Stage[]>;
  layout?: FormLayout;
  selectChildLayout: typeof selectChildLayout;
}

const messages = defineMessages({
  back: {
    id: "settings.formLayouts.modal.variants.banner.back",
    defaultMessage: "Back to Primary",
    description: "Variant banner back button title",
  },
  title: {
    id: "settings.formLayouts.modal.variants.banner.title",
    defaultMessage: "You are editing a variant",
    description: "Variant banner title",
  },
});

const VariantBanner: React.FC<Props> = ({
  activeChildLayout,
  activityTypes,
  entityType,
  funnelStages,
  layout,
  selectChildLayout,
}) => {
  const intl = useIntl();
  const isDeal = entityType === EntityType.DEAL;

  const childLayout = useMemo(() => {
    if (activeChildLayout && layout?.childLayouts?.length) {
      return layout.childLayouts.find((child) => child.id === activeChildLayout);
    }

    return undefined;
  }, [activeChildLayout, layout]);

  const activityType = useMemo(() => {
    if (!isDeal && childLayout?.crmActivityType?.id) {
      return (activityTypes ?? []).find((type) => type.id === childLayout.crmActivityType!.id);
    }

    return undefined;
  }, [activityTypes, childLayout, isDeal]);

  const stage = useMemo(() => {
    if (isDeal && childLayout?.funnel?.id && childLayout?.stage?.id) {
      const funnel = funnelStages[childLayout.funnel.id];
      return funnel?.find((stage) => stage.id === childLayout.stage!.id);
    }

    return undefined;
  }, [childLayout, funnelStages, isDeal]);
  const stageColor = stage?.color ? anyColorToHex(stage.color) : colors.slate;

  const handleBackClick = useCallback(() => selectChildLayout(undefined), [selectChildLayout]);

  if (!childLayout) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.text}>{intl.formatMessage(messages.title)}</div>
        {isDeal ? (
          <Tooltip placement="top" title={stage?.funnel.name}>
            <Tag className={styles.tag}>
              <Badge color={stageColor} />
              <span className={styles.name}>{stage?.name}</span>
            </Tag>
          </Tooltip>
        ) : activityType ? (
          <Tag className={styles.tag}>
            <ActivityTypeIcon
              activityType={activityType}
              className={styles.type}
              size="sm"
              tooltipProps={false}
            />
            <span className={styles.name}>{activityType.name}</span>
          </Tag>
        ) : null}

        <div className={styles.action}>
          <Button onClick={handleBackClick}>{intl.formatMessage(messages.back)}</Button>
        </div>
      </div>
      <ActivityRequiredRelationshipAlert />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  activeChildLayout: getEditedChildLayoutId(state),
  activityTypes: getActivityTypes(state),
  entityType: getActiveEntityType(state) as EntityTypesSupportingChildLayouts,
  funnelStages: getFunnelStages(state),
  layout: getEditedLayout(state),
});

const mapDispatchToProps = {
  selectChildLayout,
};

export default connect(mapStateToProps, mapDispatchToProps)(VariantBanner);
